import Image from 'next/image';
import Link from 'next/link';

import { LoadingInner } from '~/_legacy/components/molecules/loading/LoadingInner';
import { MBMap } from '~/_legacy/components/organisms/achievement/MBMap';

import { V1CorporatesLocationsIndex200CorporateLocationsItem } from '~/api/_openapi/schemas';
import { ApiCompetitionShow } from '~/api/v1/organizations/[id]/competitions/get';
import { ApiOrganizationShow } from '~/api/v1/organizations/[id]/get';
import { CardProject } from '~/components/Card/CardProject';
import { CompanyInformation } from '~/components/CompanyInformation/CompanyInformation';
import { SdgsList } from '~/components/SdgsList/SdgsList';
import { isNotEmpty } from '~/utils/array';

import IMG_INTILAQ from './intilaq.png';

type Props = {
  org: ApiOrganizationShow;
  competitions?: ApiCompetitionShow[];
  isLoadingOrgCompetitions?: boolean;
  orgLocations?: V1CorporatesLocationsIndex200CorporateLocationsItem[];
};

export function TabPanelHome({ org, competitions, isLoadingOrgCompetitions, orgLocations }: Props) {
  const activeCompetitions = competitions?.filter((c) => c.status !== 'finished');

  return (
    /**
     * NOTE: 本来、articleに `max-w-[960px]` を付与したいが、
     * carouselの幅が100vwになるため、各wrapperに `lg:max-w-[960px]` を付与している
     */
    <article className='flex w-full flex-col gap-xxl__sp py-10 lg:gap-xxl lg:py-16'>
      {/* contents-vision-wrapper */}
      <div className='flex w-full flex-col gap-xl__sp lg:mx-auto lg:max-w-[960px] lg:gap-xl'>
        <h2 className='text-title-x-large__sp font-bold text-accent lg:text-title-x-large lg:font-bold'>
          {org.vision}
        </h2>
        <p className='whitespace-pre-wrap text-body-medium text-regular'>{org.mission}</p>
      </div>

      {/* contents-business-summary-wrapper */}
      {isNotEmpty(org.activity) && (
        <div className='flex w-full flex-col gap-xl__sp lg:mx-auto lg:max-w-[960px] lg:gap-xl'>
          <h3 className='text-title-medium__sp font-bold text-accent lg:text-title-medium lg:font-bold'>
            事業概要
          </h3>
          <p className='whitespace-pre-wrap text-body-medium text-regular'>{org.activity}</p>
        </div>
      )}

      {/* contents-social-issue-wrapper */}
      {isNotEmpty(org.social_issue) && (
        <div className='flex w-full flex-col gap-xl__sp lg:mx-auto lg:max-w-[960px] lg:gap-xl'>
          <h3 className='text-title-medium__sp font-bold text-accent lg:text-title-medium lg:font-bold'>
            解決したい社会課題
          </h3>

          <p className='whitespace-pre-wrap text-body-medium text-regular'>{org.social_issue}</p>
        </div>
      )}

      {/* contents-sdgs-wrapper */}
      {isNotEmpty(org.sdgs) && (
        <div className='flex w-full flex-col gap-xl__sp lg:mx-auto lg:max-w-[960px] lg:gap-xl'>
          <h3 className='text-title-medium__sp font-bold text-accent lg:text-title-medium lg:font-bold'>
            注力するSDGs目標
          </h3>
          <SdgsList sdgs={org.sdgs} />
        </div>
      )}

      {/* contents-projects-wrapper */}
      {isNotEmpty(activeCompetitions) && (
        <div className='flex flex-col gap-xl__sp lg:gap-xl'>
          <h3 className='w-full text-title-medium__sp font-bold text-accent lg:mx-auto lg:max-w-[960px] lg:text-title-medium lg:font-bold'>
            募集中のプロジェクト
            {/* TODO: 新規APIが作成した時点で置き換え */}
          </h3>
          {isLoadingOrgCompetitions ? (
            <div className='lg:mx-auto lg:max-w-[960px]'>
              <LoadingInner isSmallSpinner height='100px' width='100px' />
            </div>
          ) : (
            <div className='w-screen overflow-x-scroll lg:pl-[calc((100vw-960px)/2)]'>
              <ul className='flex w-max flex-nowrap gap-l__sp pb-2 pl-1 pr-8 lg:gap-l'>
                {activeCompetitions.map((competition) => (
                  <li key={`competition-${competition.id}`}>
                    <CardProject
                      title={competition.title ?? ''}
                      src={competition.cover ?? '/dummy/900-600.png'}
                      link={`/comps/${competition.id}`}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* contents-area-wrapper */}
      {isNotEmpty(orgLocations) && (
        <div className='flex w-full flex-col gap-xl__sp lg:mx-auto lg:max-w-[960px] lg:gap-xl'>
          <h3 className='text-title-medium__sp font-bold text-accent lg:text-title-medium lg:font-bold'>
            活動エリア
          </h3>
          <div className='h-[360px] w-full'>
            <MBMap ownerLocations={orgLocations} className='h-[360px] w-full' minZoom={3.4} />
          </div>
        </div>
      )}

      {/* contents-company-info-wrapper */}
      <div className='flex w-full flex-col gap-xl__sp lg:mx-auto lg:max-w-[960px] lg:gap-xl'>
        <h3 className='text-title-medium__sp font-bold text-accent lg:text-title-medium lg:font-bold'>
          組織情報
        </h3>

        <CompanyInformation ownerType='Organization' company={org} />
      </div>

      {org.intilaq && (
        <div className='flex w-full flex-col gap-xl__sp lg:mx-auto lg:max-w-[960px] lg:gap-xl'>
          <h3 className='text-title-medium__sp font-bold text-accent lg:text-title-medium lg:font-bold'>
            参加するコミュニティ
          </h3>

          <Link href={'/reports/intilaq'}>
            <Image src={IMG_INTILAQ} alt='intilaq' width={320} className='mt-4' />
          </Link>
        </div>
      )}
    </article>
  );
}
